import React, { useState } from 'react';
import { ClientUserRole, CreateClientUserPayload } from '../types/superadmin';

/**
 * Create-user form for a client. Same fields and same validation rules as the
 * tenant admin portal's "Add User" dialog (`admin-portal/src/pages/Users.tsx`)
 * so a user created by a super admin is indistinguishable from one created by
 * the client's own admin.
 */

const MIN_PASSWORD_LENGTH = 6;

const ROLE_OPTIONS: { value: ClientUserRole; label: string; hint: string }[] = [
  { value: 'user', label: 'User', hint: 'Salesperson — web app and desktop apps.' },
  { value: 'admin', label: 'Admin', hint: 'Client administrator — full admin portal access.' }
];

const EMPTY_FORM = {
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
  role: 'user' as ClientUserRole
};

interface AddClientUserFormProps {
  clientName: string;
  submitting: boolean;
  onSubmit: (payload: CreateClientUserPayload) => Promise<void>;
  onCancel: () => void;
}

export default function AddClientUserForm({
  clientName,
  submitting,
  onSubmit,
  onCancel
}: AddClientUserFormProps) {
  const [form, setForm] = useState(EMPTY_FORM);

  const setField = (field: keyof typeof EMPTY_FORM, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD_LENGTH;
  const passwordsMismatch =
    form.confirmPassword.length > 0 && form.password !== form.confirmPassword;

  const canSubmit =
    !submitting &&
    Boolean(form.email && form.name && form.password && form.confirmPassword) &&
    !passwordTooShort &&
    !passwordsMismatch;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    await onSubmit({
      email: form.email.trim(),
      name: form.name.trim(),
      password: form.password,
      role: form.role
    });
    setForm(EMPTY_FORM);
  };

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      <p className="desc" style={{ marginBottom: 14 }}>
        The new user signs in with this email and password at {clientName}. Share the password
        with them directly — it is not emailed.
      </p>

      <div className="user-form-grid">
        <label className="field">
          <span>Full name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Jamie Rivera"
            autoComplete="off"
            required
          />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            placeholder="jamie@client.com"
            autoComplete="off"
            required
          />
        </label>

        <label className="field">
          <span>Role</span>
          <select value={form.role} onChange={(e) => setField('role', e.target.value)}>
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <small>{ROLE_OPTIONS.find((o) => o.value === form.role)?.hint}</small>
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setField('password', e.target.value)}
            autoComplete="new-password"
            required
          />
          <small className={passwordTooShort ? 'field-err' : undefined}>
            At least {MIN_PASSWORD_LENGTH} characters.
          </small>
        </label>

        <label className="field">
          <span>Confirm password</span>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setField('confirmPassword', e.target.value)}
            autoComplete="new-password"
            required
          />
          {passwordsMismatch && <small className="field-err">Passwords do not match.</small>}
        </label>
      </div>

      <div className="user-form-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {submitting ? 'Creating…' : 'Create user'}
        </button>
      </div>
    </form>
  );
}
